/* ============================================================
   Dathent — UpgradeContext
   Global provider that exposes openUpgrade({ feature }) / closeUpgrade
   and renders the focused UpgradeModal once, so any screen can surface
   the conversion modal without threading props or duplicating the modal.
   Mirrors the ActivityProvider pattern — mounted once around the routed
   screens (see App.jsx /app route).
   ============================================================ */
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import UpgradeModal from '../components/UpgradeModal.jsx';
import { track } from '../lib/funnel.js';
import { useFreePreview } from './FreePreviewContext.jsx';
import { wallSourceForFeature } from '../lib/freePreview.js';

const UpgradeContext = createContext({
  openUpgrade: () => {},
  closeUpgrade: () => {},
});

export function UpgradeProvider({ children }) {
  const [state, setState] = useState({ open: false, feature: 'default', variant: 'upgrade' });
  // GH-1133 S5 (design §2.5 choke points): inside the free preview every
  // upgrade CTA opens the trial-aware wall instead of UpgradeModal, whose
  // paths start a no-trial checkout for a 'new' account. Outside the preview
  // the default context is inactive and nothing below changes.
  const freePreview = useFreePreview();
  const freePreviewActive = freePreview.active;
  const openTrialWall = freePreview.openTrialWall;

  // Every screen's "Upgrade"/quota-limit/paywall CTA funnels through this one
  // provider (see the openUpgrade call sites across the app), which makes it
  // the single choke point for paywall_shown/paywall_dismissed (TASK-122) —
  // no need to instrument each call site individually. `feature` tags WHICH
  // gate triggered it (quota, research, autopilot, preview, default, ...).
  // TASK-331: `variant` ('upgrade' | 'renew') picks which face of the ONE
  // UpgradeModal to show — 'renew' for a PLAN_LAPSED 402 (a lapsed customer
  // is renewing something they already paid for, never "upgrading"/starting
  // a new trial). Defaults to 'upgrade' so every existing call site with no
  // opinion on this renders byte-identical to before TASK-331.
  const openUpgrade = useCallback((opts = {}) => {
    if (freePreviewActive) {
      openTrialWall(wallSourceForFeature(opts.feature), { feature: opts.feature || null });
      return;
    }
    const feature = opts.feature || 'default';
    const variant = opts.variant === 'renew' ? 'renew' : 'upgrade';
    setState({ open: true, feature, variant });
    try { track('paywall_shown', { feature, variant }); } catch { /* fail-silent */ }
  }, [freePreviewActive, openTrialWall]);

  const closeUpgrade = useCallback(() => {
    setState((s) => {
      // Only a REAL dismiss (it was actually open) counts — onClose can fire
      // from UpgradeModal's Esc/backdrop handlers even in states that don't
      // change anything meaningful; don't inflate the funnel with no-ops.
      if (s.open) {
        try { track('paywall_dismissed', { feature: s.feature }); } catch { /* fail-silent */ }
      }
      return { ...s, open: false };
    });
  }, []);

  const value = useMemo(() => ({ openUpgrade, closeUpgrade }), [openUpgrade, closeUpgrade]);

  return (
    <UpgradeContext.Provider value={value}>
      {children}
      <UpgradeModal open={state.open} feature={state.feature} variant={state.variant} onClose={closeUpgrade} />
    </UpgradeContext.Provider>
  );
}

export function useUpgrade() {
  return useContext(UpgradeContext);
}

export default UpgradeProvider;
